import { useRef, useCallback } from "react";
import Image from "next/image";
import { animate, motion, useMotionValue } from "motion/react";
import styles from "./HeroCanvas.module.scss";
import { useAppContext } from "../../../context/AppContext";
import useIsomorphicLayoutEffect from "@/hooks/useIsomorphicLayoutEffect";

interface HeroCanvasProps {
  imageSrc: string;
  imageAlt?: string;
  isNobleElements: boolean;
  hidePromoboxCutout?: boolean;
}

const PADDING_X = 16;
const PADDING_Y = 16;
const NAVBAR_BREAKPOINT = 1216;
const DESKTOP_BREAKPOINT = 1024;
const HERO_CUTOUT_INTRO_START_DELAY = 120;
const HERO_CUTOUT_INTRO_DURATION = 850;
const HERO_CUTOUT_INTRO_EASING = [0.455, 0.03, 0.515, 0.955] as const;
const NAVBAR_FADE_START_DELAY = 50;
const HERO_CUTOUT_STABLE_LAYOUT_FRAMES = 3;
// Hard failsafe: the navbar must never stay hidden, whatever happens above.
const HERO_CUTOUT_WATCHDOG = 3000;

const NAVBAR_FALLBACK_WIDTH = 919;
const NAVBAR_FALLBACK_HEIGHT = 72;
const NAVBAR_ELEMENT_ID = "navbar-main";

type HeroPathPhase = "intro" | "final";
type HeroIntroState = "pending" | "running" | "done";

/** Live navbar box, used while the context value is still {0,0}. */
const measureNavbar = () => {
  if (typeof document === "undefined") return null;
  const navbar = document.getElementById(NAVBAR_ELEMENT_ID);
  if (!navbar) return null;
  const { offsetWidth, offsetHeight } = navbar;
  if (!offsetWidth || !offsetHeight) return null;
  return { width: offsetWidth, height: offsetHeight };
};

/**
 * HeroCanvas - Responsive masked hero image using CSS clip-path
 *
 * Replaces the old Canvas implementation for better performance and native image loading.
 * Features:
 * - A stepped notch at the top-right area
 * - An optional square cutout at the bottom-right (visible only on desktop+)
 * - Fixed corner radii that stay consistent at all screen sizes
 *
 * @see HeroCanvas.md for detailed documentation on tweakable values
 */
export const HeroCanvas = ({
  imageSrc,
  imageAlt = "Hero image",
  isNobleElements,
  hidePromoboxCutout = false,
}: HeroCanvasProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const pathPhaseRef = useRef<HeroPathPhase>("final");
  const introStateRef = useRef<HeroIntroState>("pending");
  const introAnimationRef = useRef<{ stop: () => void } | null>(null);
  const introSizeRef = useRef<{ width: number; height: number } | null>(null);
  const clipPath = useMotionValue("none");
  const { navbarSize, setIsHomeIntroComplete } = useAppContext();
  // navbarSize is {0,0} until the navbar measured itself. Read the element
  // directly in that window – guessing produces a notch wider than the navbar.
  const measuredNavbar = measureNavbar();
  const navbarWidth =
    (navbarSize?.width || measuredNavbar?.width || NAVBAR_FALLBACK_WIDTH) +
    PADDING_X;
  const navbarHeight =
    (navbarSize?.height || measuredNavbar?.height || NAVBAR_FALLBACK_HEIGHT) +
    PADDING_Y;

  const CORNER_RADIUS = 20;

  const generatePath = useCallback(
    (w: number, h: number, phase: HeroPathPhase) => {
      const r = CORNER_RADIUS;

      const isWidescreen = window.innerWidth >= NAVBAR_BREAKPOINT;
      const isDesktopOrAbove = window.innerWidth >= DESKTOP_BREAKPOINT;
      const isIntro =
        phase === "intro" &&
        isWidescreen &&
        isDesktopOrAbove &&
        !hidePromoboxCutout;

      const notchX = isWidescreen ? w - navbarWidth : w - (127.1 + PADDING_X);
      const notchY = isWidescreen ? navbarHeight : 70 + PADDING_Y;
      const pathNotchX = isIntro ? w : notchX;
      const pathNotchY = notchY;

      const cutoutSize = 313;
      const cutoutX = w - cutoutSize;
      const cutoutY = h - cutoutSize;
      const pathCutoutX = cutoutX;
      const pathCutoutY = cutoutY;

      let path = `M ${r} 0 `;

      // Top-left to notch
      path += `L ${Math.max(r, pathNotchX - r)} 0 `;
      // Notch outer corner (step down)
      path += `A ${r} ${r} 0 0 1 ${pathNotchX} ${r} `;
      // Notch vertical line down
      path += `L ${pathNotchX} ${Math.max(r, pathNotchY - r)} `;
      // Notch inner corner (step continues right)
      path += `A ${r} ${r} 0 0 0 ${pathNotchX + r} ${pathNotchY} `;

      if (hidePromoboxCutout) {
        // Short widescreen: merge navbar notch with promobox cut into a single
        // L-shape (no pill on the right edge between navbar and promobox).
        path += `L ${Math.max(pathNotchX + r, pathCutoutX - r)} ${pathNotchY} `;
        path += `A ${r} ${r} 0 0 1 ${pathCutoutX} ${pathNotchY + r} `;
        path += `L ${pathCutoutX} ${Math.max(pathNotchY + r, h - r)} `;
        path += `A ${r} ${r} 0 0 1 ${pathCutoutX - r} ${h} `;
      } else {
        // Notch horizontal to right edge
        path += `L ${Math.max(pathNotchX + r, w - r)} ${pathNotchY} `;
        // Top-right corner
        path += `A ${r} ${r} 0 0 1 ${w} ${pathNotchY + r} `;

        if (isDesktopOrAbove) {
          // Right edge down to cutout
          path += `L ${w} ${Math.max(pathNotchY + r, pathCutoutY - r)} `;
          path += `A ${r} ${r} 0 0 1 ${w - r} ${pathCutoutY} `;

          // Cutout top edge left
          path += `L ${pathCutoutX + r} ${pathCutoutY} `;
          path += `A ${r} ${r} 0 0 0 ${pathCutoutX} ${pathCutoutY + r} `;

          // Cutout left edge down
          path += `L ${pathCutoutX} ${Math.max(pathCutoutY + r, h - r)} `;
          path += `A ${r} ${r} 0 0 1 ${pathCutoutX - r} ${h} `;
        } else {
          // Mobile/Tablet: Simple rounded bottom-right corner
          path += `L ${w} ${Math.max(pathNotchY + r, h - r)} `;
          path += `A ${r} ${r} 0 0 1 ${w - r} ${h} `;
        }
      }

      // Bottom edge
      path += `L ${r} ${h} `;
      // Bottom-left corner
      path += `A ${r} ${r} 0 0 1 0 ${h - r} `;

      // Left edge
      path += `L 0 ${r} `;
      // Top-left corner
      path += `A ${r} ${r} 0 0 1 ${r} 0 `;

      path += `Z`;

      return path;
    },
    [navbarWidth, navbarHeight, hidePromoboxCutout],
  );

  /**
   * Writes the clip-path for the given phase. Returns false when the container
   * has no usable size yet (hydration / not laid out), so callers can retry.
   */
  const updatePath = useCallback(
    (phase?: HeroPathPhase) => {
      if (!containerRef.current) return false;
      const rect = containerRef.current.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return false;

      const nextPhase = phase ?? pathPhaseRef.current;
      pathPhaseRef.current = nextPhase;
      clipPath.set(
        `path('${generatePath(rect.width, rect.height, nextPhase)}')`,
      );
      return true;
    },
    [clipPath, generatePath],
  );

  // Keep the intro effect free of changing deps (navbarSize arrives late and
  // would otherwise restart – and repeatedly abort – the intro sequence).
  const updatePathRef = useRef(updatePath);
  updatePathRef.current = updatePath;
  const generatePathRef = useRef(generatePath);
  generatePathRef.current = generatePath;
  // A first non-zero navbar measurement can still use fallback font metrics.
  // Wait until the context value and the live DOM box agree before keyframing.
  const navbarSizeRef = useRef(navbarSize);
  navbarSizeRef.current = navbarSize;
  const setIsHomeIntroCompleteRef = useRef(setIsHomeIntroComplete);
  setIsHomeIntroCompleteRef.current = setIsHomeIntroComplete;
  const hidePromoboxCutoutRef = useRef(hidePromoboxCutout);
  hidePromoboxCutoutRef.current = hidePromoboxCutout;

  // Intro sequence – runs exactly once per mount. The clip path is a Motion
  // value, so the first keyframe is applied synchronously and Framer Motion
  // owns the complete morph instead of racing React state during hydration.
  useIsomorphicLayoutEffect(() => {
    if (introStateRef.current !== "pending") return;

    const canAnimate =
      window.innerWidth >= NAVBAR_BREAKPOINT &&
      !hidePromoboxCutoutRef.current &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // Keep path writes on the intro shape while the final navbar measurement
    // is pending, otherwise the resize effect can paint the final notch first.
    pathPhaseRef.current = canAnimate ? "intro" : "final";

    const finish = () => {
      if (introStateRef.current === "done") return;
      introStateRef.current = "done";
      introAnimationRef.current?.stop();
      introAnimationRef.current = null;
      updatePathRef.current("final");
      setIsHomeIntroCompleteRef.current(true);
    };

    if (!canAnimate) {
      finish();
      return;
    }

    introStateRef.current = "running";
    setIsHomeIntroCompleteRef.current(false);
    updatePathRef.current("intro");

    let frameId = 0;
    let navbarTimerId: number | undefined;
    let stableLayoutFrames = 0;
    let previousLayoutSize: {
      heroWidth: number;
      heroHeight: number;
      navbarWidth: number;
      navbarHeight: number;
    } | null = null;

    const startWhenLaidOut = () => {
      if (introStateRef.current !== "running") return;

      const el = containerRef.current;
      const rect = el?.getBoundingClientRect();
      const liveNavbarSize = measureNavbar();
      const contextNavbarSize = navbarSizeRef.current;

      const isLaidOut = Boolean(rect && rect.width > 0 && rect.height > 0);
      const areFontsReady =
        !document.fonts || document.fonts.status === "loaded";
      const isNavbarSizeFinal = Boolean(
        liveNavbarSize &&
        contextNavbarSize?.width &&
        contextNavbarSize?.height &&
        Math.abs(liveNavbarSize.width - contextNavbarSize.width) <= 1 &&
        Math.abs(liveNavbarSize.height - contextNavbarSize.height) <= 1,
      );

      if (
        !el ||
        !rect ||
        !liveNavbarSize ||
        !isLaidOut ||
        !areFontsReady ||
        !isNavbarSizeFinal
      ) {
        stableLayoutFrames = 0;
        previousLayoutSize = null;
        frameId = requestAnimationFrame(startWhenLaidOut);
        return;
      }

      const currentLayoutSize = {
        heroWidth: rect.width,
        heroHeight: rect.height,
        navbarWidth: liveNavbarSize.width,
        navbarHeight: liveNavbarSize.height,
      };
      const isLayoutStable = Boolean(
        previousLayoutSize &&
        Math.abs(previousLayoutSize.heroWidth - currentLayoutSize.heroWidth) <=
          1 &&
        Math.abs(
          previousLayoutSize.heroHeight - currentLayoutSize.heroHeight,
        ) <= 1 &&
        Math.abs(
          previousLayoutSize.navbarWidth - currentLayoutSize.navbarWidth,
        ) <= 1 &&
        Math.abs(
          previousLayoutSize.navbarHeight - currentLayoutSize.navbarHeight,
        ) <= 1,
      );

      previousLayoutSize = currentLayoutSize;
      stableLayoutFrames = isLayoutStable ? stableLayoutFrames + 1 : 0;

      if (stableLayoutFrames < HERO_CUTOUT_STABLE_LAYOUT_FRAMES) {
        frameId = requestAnimationFrame(startWhenLaidOut);
        return;
      }

      const introPath = `path('${generatePathRef.current(
        rect.width,
        rect.height,
        "intro",
      )}')`;
      const finalPath = `path('${generatePathRef.current(
        rect.width,
        rect.height,
        "final",
      )}')`;

      pathPhaseRef.current = "final";
      introSizeRef.current = { width: rect.width, height: rect.height };
      clipPath.set(introPath);

      const onDone = () => {
        navbarTimerId = window.setTimeout(finish, NAVBAR_FADE_START_DELAY);
      };
      introAnimationRef.current = animate(clipPath, [introPath, finalPath], {
        type: "tween",
        duration: HERO_CUTOUT_INTRO_DURATION / 1000,
        delay: HERO_CUTOUT_INTRO_START_DELAY / 1000,
        ease: HERO_CUTOUT_INTRO_EASING,
        onComplete: onDone,
      });
    };

    startWhenLaidOut();
    const watchdogId = window.setTimeout(finish, HERO_CUTOUT_WATCHDOG);

    return () => {
      cancelAnimationFrame(frameId);
      window.clearTimeout(watchdogId);
      if (navbarTimerId) window.clearTimeout(navbarTimerId);
      if (introAnimationRef.current) {
        introAnimationRef.current.stop();
        introAnimationRef.current = null;
      }
      // Never leave the intro half-finished (navbar would stay hidden).
      if (introStateRef.current === "running") {
        introStateRef.current = "done";
        setIsHomeIntroCompleteRef.current(true);
      }
    };
  }, []);

  // Recompute the path on resize and whenever the navbar dimensions change.
  useIsomorphicLayoutEffect(() => {
    let animationFrameId = 0;

    // Do not paint a final clip before Motion has applied its intro keyframe.
    if (introStateRef.current !== "running" || introAnimationRef.current) {
      updatePath();
    }

    const scheduleUpdate = () => {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = requestAnimationFrame(() => {
        // A running intro is keyframed against the size it started with – drop
        // it if the box actually changed, instead of morphing to a stale shape.
        // (The ResizeObserver also fires once on observe, hence the compare.)
        const rect = containerRef.current?.getBoundingClientRect();
        const introSize = introSizeRef.current;
        if (introAnimationRef.current && introStateRef.current === "running") {
          const didSizeChange = Boolean(
            rect &&
            introSize &&
            (Math.abs(rect.width - introSize.width) > 1 ||
              Math.abs(rect.height - introSize.height) > 1),
          );

          if (!didSizeChange) return;

          introAnimationRef.current.stop();
          introAnimationRef.current = null;
          introStateRef.current = "done";
          setIsHomeIntroCompleteRef.current(true);
        }
        updatePath();
      });
    };

    const resizeObserver = new ResizeObserver(scheduleUpdate);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    window.addEventListener("resize", scheduleUpdate);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", scheduleUpdate);
      cancelAnimationFrame(animationFrameId);
    };
  }, [updatePath]);

  return (
    <motion.div
      ref={containerRef}
      className={styles["hero-canvas-container"]}
      style={{
        clipPath,
        WebkitClipPath: clipPath,
        transform: "translateZ(0)",
      }}
    >
      <Image
        src={imageSrc}
        alt={imageAlt}
        className={styles["hero-image"]}
        fill
        priority
        quality={100}
        sizes="100vw"
      />
      {!isNobleElements && <div className={styles["hero-overlay"]}></div>}
    </motion.div>
  );
};
